import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  Box,
  InputBase,
  Typography,
  Stack,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Search,
  Close,
  BuildOutlined,
  ArticleOutlined,
  PublicOutlined,
  LocalOfferOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors, radii } from '../../styles/tokens';
import { fetchSearch, SearchResults } from '../../services/articlesApi';
import { trackEvent } from '../../services/analytics';

const OPEN_EVENT = 'gck:open-search';

// Imperatively open the global search dialog from anywhere (e.g. the hero search).
export function openGlobalSearch(initialQuery = '', source = 'hero'): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { initialQuery, source } }));
}

type FlatResult =
  | { kind: 'tool'; label: string; sub: string; path: string }
  | { kind: 'article'; label: string; sub: string; path: string }
  | { kind: 'country'; label: string; sub: string; path: string }
  | { kind: 'topic'; label: string; sub: string; path: string };

const EMPTY: SearchResults = { tools: [], articles: [], countries: [], topics: [] };

function flatten(r: SearchResults): FlatResult[] {
  return [
    ...r.tools.map((t): FlatResult => ({ kind: 'tool', label: t.name, sub: 'Tool', path: t.path })),
    ...r.articles.map((a): FlatResult => ({ kind: 'article', label: a.title, sub: a.category, path: `/news/${a.slug}` })),
    ...r.countries.map((c): FlatResult => ({ kind: 'country', label: c.name, sub: 'Country', path: '/details' })),
    ...r.topics.map((t): FlatResult => ({ kind: 'topic', label: t, sub: 'Topic', path: `/news/topic/${encodeURIComponent(t)}` })),
  ];
}

const KIND_ICON: Record<FlatResult['kind'], React.ReactElement> = {
  tool: <BuildOutlined sx={{ fontSize: 18 }} />,
  article: <ArticleOutlined sx={{ fontSize: 18 }} />,
  country: <PublicOutlined sx={{ fontSize: 18 }} />,
  topic: <LocalOfferOutlined sx={{ fontSize: 18 }} />,
};

export const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const flat = useMemo(() => flatten(results), [results]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setResults(EMPTY);
    setActiveIndex(0);
  }, []);

  // Global open triggers: Ctrl/Cmd+K and the custom event from the hero search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        trackEvent('search_open', { source: 'hotkey' });
      }
    };
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { initialQuery?: string; source?: string }
        | undefined;
      setOpen(true);
      if (detail?.initialQuery) setQuery(detail.initialQuery);
      trackEvent('search_open', { source: detail?.source ?? 'hero' });
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen as EventListener);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen as EventListener);
    };
  }, []);

  // Debounced search.
  useEffect(() => {
    const q = query.trim();
    if (q.length === 0) {
      setResults(EMPTY);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      let cancelled = false;
      fetchSearch(q)
        .then((r) => {
          if (cancelled) return;
          setResults(r);
          // Length and hit count only -- the query text itself is never sent.
          const count = flatten(r).length;
          trackEvent('search_query', {
            query_length: q.length,
            result_count: count,
            tool_outcome: count > 0 ? 'success' : 'empty',
          });
        })
        .catch(() => {
          if (cancelled) return;
          setResults(EMPTY);
          trackEvent('search_query', { query_length: q.length, tool_outcome: 'error' });
        })
        .finally(() => !cancelled && setLoading(false));
      return () => { cancelled = true; };
    }, 200);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => setActiveIndex(0), [results]);

  const go = useCallback((r: FlatResult) => {
    trackEvent('search_select', {
      result_kind: r.kind,
      result_position: flat.indexOf(r) + 1,
      query_length: query.trim().length,
    });
    navigate(r.path);
    close();
  }, [navigate, close, flat, query]);

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flat[activeIndex]) {
      e.preventDefault();
      go(flat[activeIndex]);
    }
  };

  const showNoResults = query.trim().length > 0 && !loading && flat.length === 0;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      disableRestoreFocus
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      PaperProps={{
        sx: { borderRadius: `${radii.card}px`, position: 'absolute', top: 80, m: 0, overflow: 'hidden' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, borderBottom: `1px solid ${colors.border}` }}>
        <Search sx={{ color: colors.textMuted }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder="Search tools, countries, news, or topics…"
          sx={{ flex: 1, fontSize: '1rem', color: colors.textPrimary }}
        />
        {loading && <CircularProgress size={18} />}
        <IconButton
          onClick={close}
          aria-label="Close search"
          size="small"
          sx={{ color: colors.textMuted, ml: 0.5 }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ maxHeight: 420, overflowY: 'auto', py: 1 }}>
        {flat.length === 0 && !showNoResults && (
          <Typography variant="body2" sx={{ color: colors.textMuted, px: 2.5, py: 3, textAlign: 'center' }}>
            Start typing to search across tools, countries, and news.
          </Typography>
        )}
        {showNoResults && (
          <Typography variant="body2" sx={{ color: colors.textMuted, px: 2.5, py: 3, textAlign: 'center' }}>
            No results for “{query}”. Try a tool name, country, or topic.
          </Typography>
        )}
        <Stack>
          {flat.map((r, i) => (
            <Box
              key={`${r.kind}-${r.path}-${i}`}
              onClick={() => go(r)}
              onMouseEnter={() => setActiveIndex(i)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2.5,
                py: 1.25,
                cursor: 'pointer',
                bgcolor: i === activeIndex ? alpha(colors.primary, 0.08) : 'transparent',
              }}
            >
              <Box sx={{ color: colors.primary, display: 'flex' }}>{KIND_ICON[r.kind]}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.label}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: colors.textMuted, flexShrink: 0 }}>{r.sub}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Dialog>
  );
};
